/**
 * @fileoverview Super admin dashboard page.
 * Displays all organizations, all users, system configuration, and billing overview.
 * Only accessible to super_admin users via a dynamic URL path.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Toast from '../components/Toast';
import Loading from '../components/Loading';
import Pagination from '../components/Pagination';
import AdminDataBrowser from '../components/AdminDataBrowser';
import PriceRangesView from '../components/PriceRangesView';
import SuperAdminAnalytics from './SuperAdminAnalytics';
import SuperAdminCrm from './SuperAdminCrm';
import AdminSidebarGroup from '../components/AdminSidebarGroup';
import ComingSoon from '../components/ComingSoon';
import OpsAnalyticsRollupPanel from './OpsAnalyticsRollupPanel';
import SuperAdminHealth from './SuperAdminHealth';
import OpsFeedbackGithubPanel from './OpsFeedbackGithubPanel';
import OpsEmailIngestPanel from './OpsEmailIngestPanel';
import FeedbackAdmin from './FeedbackAdmin';
import AuditLogViewer from './AuditLogViewer';

/**
 * localStorage key for the sidebar accordion's per-group open/closed map.
 * Bumped only if the shape of {overview, tenants, data, analytics, operations, support}
 * ever changes; older stored blobs that fail the shape check fall back to the
 * "expand the group containing the current activeTab, collapse the rest" default.
 */
const SIDEBAR_STORAGE_KEY = 'superAdminSidebarState';

/**
 * Stable id for every sidebar accordion group. The order matches the visual
 * top-to-bottom order in the rendered sidebar and is what
 * {@link SIDEBAR_TAB_ORDER} and {@link TAB_TO_GROUP} both derive from.
 */
const SIDEBAR_GROUP_IDS = ['overview', 'tenants', 'data', 'analytics', 'operations', 'system', 'support'];

/**
 * Ordered map of {groupId → [tabId, ...]} used to build the sidebar sections,
 * the arrow-key traversal list, and the tab→group reverse lookup used by the
 * auto-expand useEffect. Adding a tab here — even in a currently-empty group
 * (Operations gains `crm` here as a placeholder for the CRM ops panel wired
 * in the next step) — is the single source of truth: the sidebar render, the
 * roving tabindex handler, and the localStorage restore all read from this
 * map.
 */
const SIDEBAR_GROUPS = {
  overview: ['dashboard'],
  tenants: ['orgs', 'users', 'billing'],
  data: [
    'invoices',
    'config',
    'price-ranges',
    'data-businesses',
    'data-vendors',
    'data-invoices',
    'data-line-items',
    'data-categories',
    'data-sku-catalog',
    'data-misc-charges',
    'data-price-history',
  ],
  analytics: ['analytics'],
  // Operations — CRM is the first real subsystem; the other four subtabs
  // render <ComingSoon /> placeholders so the accordion shape is established
  // and operators can see where the future Analytics rollup / PDF storage /
  // Feedback→GH / SES surfaces will land.
  operations: [
    'crm',
    'ops-analytics-rollup',
    'ops-pdf-storage',
    'ops-feedback-github',
    'ops-email',
    'ops-email-intake',
  ],
  // System — container + AWS managed-service health dashboard. Lives in
  // its own group so operators can bookmark it without collapsing/expanding
  // the Operations accordion every visit.
  system: ['health'],
  support: ['audit-log', 'feedback'],
};

/**
 * Flat top-to-bottom tab-id order used by the sidebar's roving-tabindex
 * arrow-key handler. Derived from SIDEBAR_GROUPS so a new tab only ever needs
 * to be added in one place.
 */
const SIDEBAR_TAB_ORDER = SIDEBAR_GROUP_IDS.flatMap((groupId) => SIDEBAR_GROUPS[groupId]);

/**
 * Reverse index: tabId → owning groupId. Used to a) collapse siblings and
 * expand the containing group on activeTab change so bookmarks like
 * /admin/analytics land with Analytics expanded, and b) resolve group state
 * updates keyed off which tab the operator just clicked.
 */
const TAB_TO_GROUP = SIDEBAR_GROUP_IDS.reduce((acc, groupId) => {
  SIDEBAR_GROUPS[groupId].forEach((tabId) => {
    acc[tabId] = groupId;
  });
  return acc;
}, {});

/**
 * Human-readable titles rendered inside each AdminSidebarGroup header. Kept
 * separate from SIDEBAR_GROUP_IDS so the id and the visible label can drift
 * independently (e.g. i18n-friendly).
 */
const SIDEBAR_GROUP_TITLES = {
  overview: 'Overview',
  tenants: 'Tenants',
  data: 'Data',
  analytics: 'Analytics',
  operations: 'Operations',
  system: 'System',
  support: 'Support',
};

/**
 * Static label for every sidebar tab whose text does not include a live
 * count. Two tabs — Organizations and Users — render "(N)" suffixes, so
 * their labels are computed from live state inside {@link renderTabLabel}
 * instead of living here.
 */
const SIDEBAR_STATIC_TAB_LABELS = {
  dashboard: 'Dashboard',
  billing: 'Billing',
  invoices: 'Invoices',
  config: 'System Config',
  'price-ranges': 'Price ranges',
  'data-businesses': 'Businesses',
  'data-vendors': 'Vendors',
  'data-invoices': 'Invoice records',
  'data-line-items': 'Line items',
  'data-categories': 'Categories',
  'data-sku-catalog': 'SKU catalog',
  'data-misc-charges': 'Misc charges',
  'data-price-history': 'Price history',
  analytics: 'Analytics',
  crm: 'CRM (Twenty)',
  'ops-analytics-rollup': 'Analytics rollup',
  'ops-pdf-storage': 'PDF storage',
  'ops-feedback-github': 'Feedback → GitHub',
  'ops-email': 'Email (SES)',
  'ops-email-intake': 'Email Intake',
  health: 'Health',
  'audit-log': 'Audit log',
  feedback: 'Feedback',
};

/**
 * Resolve the visible label for a sidebar tab. Static labels come from
 * {@link SIDEBAR_STATIC_TAB_LABELS}; the two count-bearing tabs
 * (Organizations, Users) fall through to a live-count template so the
 * sidebar reflects fresh admin data as it loads.
 *
 * @param {string} tabId - sidebar tab id (matches SIDEBAR_GROUPS entries)
 * @param {Object} liveCounts
 * @param {Array} liveCounts.orgs - loaded orgs list; used for Organizations (N)
 * @param {Array} liveCounts.users - loaded users list; used for Users (N)
 * @returns {string} label rendered inside the sidebar button
 */
function renderTabLabel(tabId, { orgs, users }) {
  if (tabId === 'orgs') return `Organizations (${orgs.length})`;
  if (tabId === 'users') return `Users (${users.length})`;
  return SIDEBAR_STATIC_TAB_LABELS[tabId] || tabId;
}

/**
 * Default sidebar accordion state: only the group containing `activeTab` is
 * expanded. Used when localStorage is empty, unparseable, or the stored blob
 * has an unexpected shape. Also used as the source of truth for the SSR/mount
 * lazy initializer so first render never flashes an inconsistent state.
 *
 * @param {string} activeTab - the tab that should land expanded
 * @returns {Object<string,boolean>} one boolean per SIDEBAR_GROUP_IDS entry
 */
function defaultSidebarState(activeTab) {
  const activeGroup = TAB_TO_GROUP[activeTab] || 'overview';
  return SIDEBAR_GROUP_IDS.reduce((acc, groupId) => {
    acc[groupId] = groupId === activeGroup;
    return acc;
  }, {});
}

/**
 * Read the sidebar accordion state from localStorage. Falls back to
 * {@link defaultSidebarState} on any of: no window, missing key, bad JSON,
 * or a payload whose shape doesn't include every group id.
 *
 * @param {string} activeTab - used when the fallback path fires
 * @returns {Object<string,boolean>} sanitized per-group open map
 */
function readSidebarState(activeTab) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return defaultSidebarState(activeTab);
  }
  try {
    const raw = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (!raw) return defaultSidebarState(activeTab);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return defaultSidebarState(activeTab);
    // Backfill any missing group id with `false` so a schema change (new
    // group added) doesn't leave a group with `undefined` state and blow up
    // the accordion.
    const normalized = { ...defaultSidebarState(activeTab) };
    SIDEBAR_GROUP_IDS.forEach((groupId) => {
      if (typeof parsed[groupId] === 'boolean') {
        normalized[groupId] = parsed[groupId];
      }
    });
    // Always guarantee the group containing the active tab is visible so a
    // bookmark restore doesn't strand the operator on an invisible tab.
    const activeGroup = TAB_TO_GROUP[activeTab];
    if (activeGroup) normalized[activeGroup] = true;
    return normalized;
  } catch {
    return defaultSidebarState(activeTab);
  }
}

/**
 * Persist the sidebar accordion state to localStorage. Silently swallows
 * write failures (private-mode Safari, quota exhaustion, etc.) — sidebar
 * usability doesn't hinge on persistence succeeding.
 *
 * @param {Object<string,boolean>} state - the full group-id → open map
 * @returns {void}
 */
function writeSidebarState(state) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore — non-critical */
  }
}

/**
 * Phase 11 W4: per-entity column + endpoint config for the SuperAdmin → Data
 * sidebar group. Each key matches a `data-<entity>` tab id (added in Wave 1)
 * and maps to:
 *   - `entity`: the URL slug for `GET /admin/data/<slug>` (Wave 3 endpoints)
 *   - `placeholder`: search-input placeholder text
 *   - `columns`: AdminDataBrowser column config (key, label, optional mono +
 *     format) — chosen to surface the most operator-useful fields per entity
 *
 * Wave 4 replaces the 8 "Coming soon" placeholder tabpanels with a single
 * parameterized render block that picks the active entity's config — so all
 * 8 cross-org Data Browser sub-pages live in this one map.
 */
const DATA_BROWSER_CONFIGS = {
  'data-businesses': {
    entity: 'businesses',
    placeholder: 'Search businesses...',
    columns: [
      { key: 'id', label: 'ID', mono: true },
      { key: 'name', label: 'Business' },
      { key: 'org_name', label: 'Organization' },
      { key: 'status', label: 'Status' },
      { key: 'created_at', label: 'Created', format: (v) => v ? new Date(v).toLocaleDateString() : '—' },
    ],
  },
  'data-vendors': {
    entity: 'vendors',
    placeholder: 'Search vendors...',
    columns: [
      { key: 'id', label: 'ID', mono: true },
      { key: 'name', label: 'Vendor' },
      { key: 'org_name', label: 'Organization' },
      { key: 'status', label: 'Status' },
    ],
  },
  'data-invoices': {
    entity: 'invoices',
    placeholder: 'Search invoices...',
    columns: [
      { key: 'invoice_number', label: 'Invoice #', mono: true },
      { key: 'invoice_date', label: 'Date', format: (v) => v ? new Date(v).toLocaleDateString() : '—' },
      { key: 'vendor_name', label: 'Vendor' },
      { key: 'business_name', label: 'Business' },
      { key: 'org_name', label: 'Organization' },
      { key: 'total_amount', label: 'Total', mono: true, format: (v) => v != null ? `$${Number(v).toFixed(2)}` : '—' },
      { key: 'status', label: 'Status' },
    ],
  },
  'data-line-items': {
    entity: 'line-items',
    placeholder: 'Search line items...',
    columns: [
      { key: 'id', label: 'ID', mono: true },
      { key: 'item_name', label: 'Item' },
      { key: 'sku', label: 'SKU', mono: true },
      { key: 'quantity', label: 'Qty', mono: true },
      { key: 'unit_price', label: 'Unit $', mono: true, format: (v) => v != null ? `$${Number(v).toFixed(2)}` : '—' },
      { key: 'total_price', label: 'Total $', mono: true, format: (v) => v != null ? `$${Number(v).toFixed(2)}` : '—' },
      { key: 'vendor_name', label: 'Vendor' },
      { key: 'org_name', label: 'Organization' },
      // Phase 11 Wave 6b — Wave 6a Variant A backend exposes canonical_unit
      // (COALESCE override + sku default) and normalized_price (unit_price /
      // base_units_per_pack with NULLIF guard). NULL when sku_catalog row is
      // missing OR base_units_per_pack isn't curated yet.
      { key: 'canonical_unit', label: 'Unit', format: (v) => v != null ? v : '—' },
      {
        key: 'normalized_price',
        label: 'Normalized $',
        mono: true,
        format: (v, row) => v == null
          ? '—'
          : `$${Number(v).toFixed(2)}/${row.canonical_unit || 'unit'}`,
      },
    ],
  },
  'data-categories': {
    entity: 'categories',
    placeholder: 'Search categories...',
    columns: [
      { key: 'id', label: 'ID', mono: true },
      { key: 'full_path', label: 'Category path' },
      { key: 'org_name', label: 'Organization' },
      { key: 'status', label: 'Status' },
    ],
  },
  'data-sku-catalog': {
    entity: 'sku-catalog',
    placeholder: 'Search SKUs...',
    columns: [
      { key: 'sku', label: 'SKU', mono: true },
      { key: 'item_name', label: 'Item' },
      { key: 'default_uom', label: 'UOM' },
      { key: 'vendor_name', label: 'Vendor' },
      { key: 'org_name', label: 'Organization' },
      { key: 'status', label: 'Status' },
      // Phase 11.1 — replaced the Phase 11 override-only columns (base_unit /
      // base_units_per_pack, almost always NULL) with the auto-derived latest
      // normalized price. Comes from the most recent price_history row's UOM
      // run through normalize_uom(). NULL only when the SKU has no price
      // history yet OR its UOM is a pack with no override.
      {
        key: 'latest_normalized_price',
        label: 'Latest $/unit',
        mono: true,
        format: (v, row) => v == null
          ? '—'
          : `$${Number(v).toFixed(2)}/${row.latest_canonical_unit || 'unit'}`,
      },
    ],
  },
  'data-misc-charges': {
    entity: 'misc-charges',
    placeholder: 'Search misc charges...',
    columns: [
      { key: 'id', label: 'ID', mono: true },
      { key: 'description', label: 'Description' },
      { key: 'amount', label: 'Amount', mono: true, format: (v) => v != null ? `$${Number(v).toFixed(2)}` : '—' },
      { key: 'vendor_name', label: 'Vendor' },
      { key: 'org_name', label: 'Organization' },
    ],
  },
  'data-price-history': {
    entity: 'price-history',
    placeholder: 'Search price history...',
    columns: [
      { key: 'item_name', label: 'Item' },
      { key: 'sku', label: 'SKU', mono: true },
      { key: 'unit_price', label: 'Unit $', mono: true, format: (v) => v != null ? `$${Number(v).toFixed(2)}` : '—' },
      { key: 'unit_of_measure', label: 'UOM' },
      { key: 'invoice_date', label: 'Invoice date', format: (v) => v ? new Date(v).toLocaleDateString() : '—' },
      { key: 'vendor_name', label: 'Vendor' },
      { key: 'org_name', label: 'Organization' },
    ],
  },
};

/**
 * Single KPI tile rendered inside the SuperAdmin dashboard's stat grid.
 *
 * @param {Object} props
 * @param {string} props.label - Short metric name (e.g. "Users")
 * @param {number|string} props.value - Big number to display
 * @param {string} [props.sub] - Optional secondary line (e.g. "+12 last 30d")
 * @returns {JSX.Element}
 */
function DashboardKpi({ label, value, sub }) {
  return (
    <div className="admin-dashboard__kpi">
      <div className="admin-dashboard__kpi-label">{label}</div>
      <div className="admin-dashboard__kpi-value">{value}</div>
      {sub ? <div className="admin-dashboard__kpi-sub">{sub}</div> : null}
    </div>
  );
}

/**
 * SuperAdmin page component.
 * Provides system-wide management for super admins.
 * Uses the dynamic admin API prefix from AuthContext for all API calls.
 *
 * @returns {JSX.Element} The super admin dashboard
 */
function SuperAdmin() {
  const { adminApiPrefix, startImpersonation, adminPath, user: currentUser, activeOrg } = useAuth();

  // Where "Back to app" sends the operator. The admin console renders OUTSIDE
  // AppContent (see App.js route table), so it has none of the normal app
  // chrome and previously offered no way out except the browser back button.
  // Prefer the active org's dashboard; fall back to the org picker at "/".
  const backToAppPath = activeOrg?.slug ? `/org/${activeOrg.slug}/dashboard` : '/';
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [config, setConfig] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [activeTab, setActiveTab] = useState('dashboard');
  // Sidebar accordion state — one boolean per group id. Lazy initializer
  // reads the persisted map from localStorage (falling back to "expand only
  // the group containing the current activeTab") so the first render is
  // already accurate and we skip the flash-then-collapse a useEffect would
  // introduce. Toggling a group updates this state AND persists the new
  // shape immediately (see toggleSidebarGroup below).
  const [sidebarState, setSidebarState] = useState(() => readSidebarState('dashboard'));
  // Mobile nav drawer state — at <=900px the sidebar collapses behind a
  // hamburger button (see App.css `.admin-mobile-nav-toggle` and
  // `.admin-sidebar--open`). useEffect below auto-closes the drawer when
  // the user picks a tab, so we don't need to wire it into every onClick.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [orgSearch, setOrgSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [orgPage, setOrgPage] = useState(1);
  const [orgPerPage, setOrgPerPage] = useState(25);
  const [userPage, setUserPage] = useState(1);
  const [userPerPage, setUserPerPage] = useState(25);
  // userOrgFilter: when set, the Orgs tab filters down to just this user's
  // org memberships. Driven by the "View Orgs" button on a user's row.
  // null = no filter active.
  const [userOrgFilter, setUserOrgFilter] = useState(null);

  // Invoices-tab state — org → business picker, paginated invoice list,
  // multi-select for bulk ZIP download.
  const [invTabOrgId, setInvTabOrgId] = useState('');
  const [invTabBusinessId, setInvTabBusinessId] = useState('');
  const [invTabBusinesses, setInvTabBusinesses] = useState([]);
  const [invTabBusinessesLoading, setInvTabBusinessesLoading] = useState(false);
  const [invTabInvoices, setInvTabInvoices] = useState([]);
  const [invTabInvoicesTotal, setInvTabInvoicesTotal] = useState(0);
  const [invTabInvoicesPage, setInvTabInvoicesPage] = useState(1);
  const [invTabInvoicesPerPage, setInvTabInvoicesPerPage] = useState(50);
  const [invTabInvoicesLoading, setInvTabInvoicesLoading] = useState(false);
  const [invTabSelected, setInvTabSelected] = useState(new Set());
  const [invTabZipLoading, setInvTabZipLoading] = useState(false);

  // System Config tab — editable draft mirror of `config`. Save button is
  // enabled only when at least one editable field differs from `config`.
  const [configDraft, setConfigDraft] = useState({});
  const [configSaving, setConfigSaving] = useState(false);

  // Auto-close the mobile nav drawer whenever the user lands on a tab.
  // No-op on initial render (mobileNavOpen is already false) — only does
  // real work when the drawer was opened by the hamburger and the user
  // tapped a tab inside it.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [activeTab]);

  // Auto-expand the accordion group that contains the current activeTab so
  // cross-tab navigation (e.g. Dashboard's "View user" link → setActiveTab
  // ('users'), or a bookmarked deep-link that lands on 'analytics') always
  // leaves the selected tab visible in the sidebar. Other groups' state is
  // untouched so an operator's manually-toggled sections stay as they were.
  // The new state is persisted immediately so a page refresh keeps the same
  // section open.
  useEffect(() => {
    const group = TAB_TO_GROUP[activeTab];
    if (!group) return;
    setSidebarState((prev) => {
      if (prev[group]) return prev;
      const next = { ...prev, [group]: true };
      writeSidebarState(next);
      return next;
    });
  }, [activeTab]);

  /**
   * Toggle one accordion group's open state and persist the new map to
   * localStorage. Multi-open semantics: toggling one group never collapses
   * any other.
   *
   * @param {string} groupId - one of SIDEBAR_GROUP_IDS
   * @returns {void}
   */
  const toggleSidebarGroup = useCallback((groupId) => {
    setSidebarState((prev) => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      writeSidebarState(next);
      return next;
    });
  }, []);

  // Sync the draft whenever the underlying `config` (re)loads — covers
  // initial load, post-save refetch, and any external updates.
  useEffect(() => {
    if (config) {
      setConfigDraft({
        super_admin_allowed_domains: config.super_admin_allowed_domains || '',
        registration_code: config.registration_code || '',
        terms_version: config.terms_version || '',
        max_users_default: config.max_users_default ?? '',
        max_businesses_default: config.max_businesses_default ?? '',
        default_plan_type: config.default_plan_type || 'free',
      });
    }
  }, [config]);

  /** True if the draft differs from the persisted config in any field. */
  const configIsDirty = useMemo(() => {
    if (!config) return false;
    return (
      String(configDraft.super_admin_allowed_domains ?? '') !== String(config.super_admin_allowed_domains ?? '') ||
      String(configDraft.registration_code ?? '') !== String(config.registration_code ?? '') ||
      String(configDraft.terms_version ?? '') !== String(config.terms_version ?? '') ||
      String(configDraft.max_users_default ?? '') !== String(config.max_users_default ?? '') ||
      String(configDraft.max_businesses_default ?? '') !== String(config.max_businesses_default ?? '') ||
      String(configDraft.default_plan_type ?? '') !== String(config.default_plan_type ?? '')
    );
  }, [config, configDraft]);

  /** Save edited config fields and refetch on success. */
  const handleSaveConfig = async () => {
    if (!adminApiPrefix || !configIsDirty) return;
    setConfigSaving(true);
    try {
      // Send only the editable keys; the backend whitelists the rest.
      const payload = {
        super_admin_allowed_domains: configDraft.super_admin_allowed_domains || '',
        registration_code: configDraft.registration_code || '',
        terms_version: configDraft.terms_version || '',
        max_users_default: configDraft.max_users_default,
        max_businesses_default: configDraft.max_businesses_default,
        default_plan_type: configDraft.default_plan_type || 'free',
      };
      await api.adminUpdateConfig(adminApiPrefix, payload);
      setToast({ show: true, message: 'System configuration saved', type: 'success' });
      // Refetch config (and orgs/users/billing — cheap on a tiny dataset).
      loadData();
    } catch (err) {
      const fields = err.response?.data?.fields;
      const msg = fields
        ? `Validation failed: ${Object.entries(fields).map(([k, v]) => `${k}: ${v}`).join('; ')}`
        : (err.response?.data?.error || 'Failed to save configuration');
      setToast({ show: true, message: msg, type: 'error' });
    } finally {
      setConfigSaving(false);
    }
  };

  /**
   * Load all admin data (orgs, users, config, billing).
   */
  const loadData = useCallback(async () => {
    if (!adminApiPrefix) {
      setToast({ show: true, message: 'Admin API not available', type: 'error' });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [orgsResp, usersResp, configResp, billingResp, dashResp] = await Promise.all([
        api.adminListOrgs(adminApiPrefix),
        api.adminListUsers(adminApiPrefix),
        api.adminGetConfig(adminApiPrefix),
        api.adminGetBilling(adminApiPrefix),
        api.adminGetDashboard(adminApiPrefix),
      ]);
      setOrgs(orgsResp.data || []);
      setUsers(usersResp.data || []);
      setConfig(configResp.data || {});
      setBilling(billingResp.data || {});
      setDashboard(dashResp.data || null);
    } catch (err) {
      // Distinguish IP-whitelist rejection (403 + reason=ip_not_allowed) from
      // every other failure. Backend only emits this shape when the request
      // is from an authenticated super admin whose source IP isn't on the
      // SUPER_ADMIN_ALLOWED_IPS list — see require_super_admin in utils/auth.py.
      const reason = err?.response?.data?.reason;
      const message = reason === 'ip_not_allowed'
        ? 'Access from this location is not authorized'
        : 'Failed to load admin data';
      setToast({ show: true, message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [adminApiPrefix]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Filtered organizations based on search text AND/OR active user-org filter.
   * userOrgFilter (when set) restricts to the org ids in userOrgFilter.orgIds;
   * the search box filter still applies on top of that.
   */
  const filteredOrgs = useMemo(() => {
    let list = orgs;
    if (userOrgFilter && Array.isArray(userOrgFilter.orgIds)) {
      const allowed = new Set(userOrgFilter.orgIds);
      list = list.filter((org) => allowed.has(org.id));
    }
    if (!orgSearch.trim()) return list;
    const query = orgSearch.toLowerCase();
    return list.filter(
      (org) =>
        org.name.toLowerCase().includes(query) ||
        org.slug.toLowerCase().includes(query)
    );
  }, [orgs, orgSearch, userOrgFilter]);

  /**
   * Dashboard → Users tab navigation: filter the Users list to a specific
   * user (by email — most stable identifier across the page) and switch tabs.
   * Triggered when a user row in the Dashboard's recent-activity lists is
   * clicked.
   *
   * @param {{id: number, email?: string, username?: string}} user - the user to focus on
   */
  const handleFocusUser = (user) => {
    if (!user) return;
    // Prefer email for the search needle (the Users-tab filter matches against
    // username, display_name, AND email), then fall back to username.
    const needle = user.email || user.username || `id ${user.id}`;
    setUserSearch(needle);
    setUserPage(1);
    setActiveTab('users');
  };

  /**
   * Activate the user-org filter and switch to the Orgs tab.
   * Clicked from a row in the Users tab. Resets the org pager to page 1.
   */
  const handleViewUserOrgs = (user) => {
    setUserOrgFilter({
      userId: user.id,
      userLabel: user.email || user.username || `user ${user.id}`,
      orgIds: Array.isArray(user.org_ids) ? user.org_ids : [],
    });
    setOrgPage(1);
    setOrgSearch('');
    setActiveTab('orgs');
  };

  /**
   * Invoices tab: when the org dropdown changes, fetch that org's businesses.
   * Resets the business picker, invoice list, selection, and pager.
   */
  const handleInvTabOrgChange = useCallback(async (orgId) => {
    setInvTabOrgId(orgId);
    setInvTabBusinessId('');
    setInvTabBusinesses([]);
    setInvTabInvoices([]);
    setInvTabInvoicesTotal(0);
    setInvTabSelected(new Set());
    setInvTabInvoicesPage(1);
    if (!orgId || !adminApiPrefix) return;
    setInvTabBusinessesLoading(true);
    try {
      const resp = await api.adminListBusinessesForOrg(adminApiPrefix, orgId);
      setInvTabBusinesses(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      setToast({ show: true, message: 'Failed to load businesses for this org', type: 'error' });
    } finally {
      setInvTabBusinessesLoading(false);
    }
  }, [adminApiPrefix]);

  /**
   * Invoices tab: load (or reload) the invoice list for the active picker.
   */
  const loadInvTabInvoices = useCallback(async (page = invTabInvoicesPage) => {
    if (!invTabOrgId || !invTabBusinessId || !adminApiPrefix) return;
    setInvTabInvoicesLoading(true);
    try {
      const resp = await api.adminListInvoicesForBusiness(
        adminApiPrefix, invTabOrgId, invTabBusinessId,
        { page, per_page: invTabInvoicesPerPage },
      );
      setInvTabInvoices(Array.isArray(resp.data?.items) ? resp.data.items : []);
      setInvTabInvoicesTotal(resp.data?.total || 0);
      setInvTabInvoicesPage(resp.data?.page || page);
    } catch (err) {
      setToast({ show: true, message: 'Failed to load invoices', type: 'error' });
    } finally {
      setInvTabInvoicesLoading(false);
    }
  }, [adminApiPrefix, invTabOrgId, invTabBusinessId, invTabInvoicesPage, invTabInvoicesPerPage]);

  // Auto-load invoices when both org+business chosen, or page/per-page changes.
  useEffect(() => {
    if (invTabOrgId && invTabBusinessId) {
      loadInvTabInvoices(invTabInvoicesPage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invTabOrgId, invTabBusinessId, invTabInvoicesPage, invTabInvoicesPerPage]);

  /**
   * Toggle one invoice in the multi-select set.
   */
  const toggleInvSelection = (invoiceId) => {
    setInvTabSelected((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId); else next.add(invoiceId);
      return next;
    });
  };

  /**
   * Toggle "select all visible" — every invoice on the current page.
   */
  const toggleInvSelectAllVisible = () => {
    const visibleIds = invTabInvoices.map((inv) => inv.id);
    const allSelected = visibleIds.every((id) => invTabSelected.has(id));
    setInvTabSelected((prev) => {
      const next = new Set(prev);
      visibleIds.forEach((id) => {
        if (allSelected) next.delete(id); else next.add(id);
      });
      return next;
    });
  };

  /**
   * POST the selected invoice ids to the bulk-ZIP endpoint and trigger
   * a browser download of the returned blob.
   */
  const handleDownloadSelectedZip = async () => {
    if (!invTabOrgId || invTabSelected.size === 0) return;
    setInvTabZipLoading(true);
    try {
      const ids = Array.from(invTabSelected);
      const resp = await api.adminDownloadInvoicesZip(adminApiPrefix, invTabOrgId, ids);
      const blob = new Blob([resp.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices-org-${invTabOrgId}-${ids.length}-files.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setToast({ show: true, message: `Downloaded ${ids.length} invoices`, type: 'success' });
    } catch (err) {
      setToast({
        show: true,
        message: err.response?.data?.error || 'Failed to download ZIP',
        type: 'error',
      });
    } finally {
      setInvTabZipLoading(false);
    }
  };

  /**
   * Single-invoice download — opens the existing /api/pdf/<org_id>/<filename>
   * endpoint in a new tab. Same shape InvoiceDetailModal uses.
   */
  const handleDownloadOne = async (orgId, pdfFilename) => {
    if (!orgId || !pdfFilename) return;
    try {
      const { default: keycloak } = await import('../services/keycloak');
      const token = keycloak.getToken && keycloak.getToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const encoded = String(pdfFilename).split('/').map(encodeURIComponent).join('/');
      const resp = await fetch(`/api/pdf/${orgId}/${encoded}`, { headers });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      setToast({ show: true, message: 'Failed to open PDF', type: 'error' });
    }
  };

  /**
   * Paginated slice of filtered organizations.
   */
  const paginatedOrgs = useMemo(() => {
    const startIndex = (orgPage - 1) * orgPerPage;
    const endIndex = startIndex + orgPerPage;
    return filteredOrgs.slice(startIndex, endIndex);
  }, [filteredOrgs, orgPage, orgPerPage]);

  /**
   * Set of email domains permitted to be granted super admin, parsed from the
   * `super_admin_allowed_domains` system setting. The Grant button on each
   * user row is disabled when the user's email domain isn't in this set —
   * mirroring the backend rule so the UX doesn't let the user click into a
   * predictable 400.
   */
  const allowedSuperAdminDomains = useMemo(() => {
    const raw = (config?.super_admin_allowed_domains || '').trim();
    if (!raw) return new Set();
    return new Set(
      raw.split(',').map((d) => d.trim().toLowerCase()).filter(Boolean)
    );
  }, [config]);

  /**
   * Filtered users based on search text.
   */
  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return users;
    const query = userSearch.toLowerCase();
    return users.filter(
      (user) =>
        (user.username || '').toLowerCase().includes(query) ||
        (user.display_name || '').toLowerCase().includes(query) ||
        (user.email || '').toLowerCase().includes(query)
    );
  }, [users, userSearch]);

  /**
   * Paginated slice of filtered users.
   */
  const paginatedUsers = useMemo(() => {
    const startIndex = (userPage - 1) * userPerPage;
    const endIndex = startIndex + userPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, userPage, userPerPage]);

  /**
   * Handle organization deletion.
   *
   * @param {number} orgId - The organization ID to delete
   * @param {string} orgName - The org name for confirmation prompt
   */
  const handleDeleteOrg = async (orgId, orgName) => {
    if (!window.confirm(`Delete organization "${orgName}"? This cannot be undone.`)) return;
    try {
      await api.adminDeleteOrg(adminApiPrefix, orgId);
      setToast({ show: true, message: `Organization "${orgName}" deleted`, type: 'success' });
      loadData();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.error || 'Failed to delete organization', type: 'error' });
    }
  };

  /**
   * Toggle a user's super-admin status.
   * Sends PATCH /admin/users/<id> with the inverted is_super_admin value
   * after a confirm prompt. The backend refuses self-revoke; the UI also
   * disables the button on the current user's own row to make this clear.
   *
   * @param {number} userId - The user id to toggle
   * @param {string} email - For the confirm prompt + toast
   * @param {boolean} currentValue - Their current is_super_admin state
   */
  const handleToggleSuperAdmin = async (userId, email, currentValue) => {
    const verb = currentValue ? 'Revoke' : 'Grant';
    const prep = currentValue ? 'from' : 'to';
    if (!window.confirm(`${verb} super admin status ${prep} ${email}?`)) return;
    try {
      await api.adminUpdateUser(adminApiPrefix, userId, { is_super_admin: !currentValue });
      setToast({
        show: true,
        message: `${verb === 'Grant' ? 'Granted' : 'Revoked'} super admin ${prep} ${email}`,
        type: 'success',
      });
      loadData();
    } catch (err) {
      setToast({
        show: true,
        message: err.response?.data?.error || `Failed to ${verb.toLowerCase()} super admin`,
        type: 'error',
      });
    }
  };

  /**
   * Roving-tabindex keyboard handler for the SuperAdmin sidebar tablist.
   * Phase 11 W1 extends the original 6-tab list to 17 sidebar items
   * (Dashboard + 5 Manage + 8 Data + 3 Intelligence) and accepts both the
   * legacy left/right arrows and the new up/down arrows since the sidebar
   * is now vertical. `currentTab` is optional; when omitted the handler
   * reads the current `activeTab` from state so callsites can pass the
   * handler bare-bones (`onKeyDown={handleTabKeyDown}`).
   *
   * @param {KeyboardEvent} e - the React keyboard event from the tab button
   * @param {string} [currentTab] - the tab id of the originating button
   *   (back-compat for the legacy horizontal callsites). When omitted we
   *   fall back to the live `activeTab` state.
   */
  const handleTabKeyDown = (e, currentTab) => {
    // Order derived from SIDEBAR_GROUPS so a new tab added to the accordion
    // (e.g. Operations → CRM) automatically joins arrow-key traversal in DOM
    // order — no separate hardcoded list to keep in sync any more.
    const tabIds = SIDEBAR_TAB_ORDER;
    const sourceTab = currentTab || activeTab;
    const currentIndex = tabIds.indexOf(sourceTab);
    if (currentIndex === -1) return;

    const isPrev = e.key === 'ArrowLeft' || e.key === 'ArrowUp';
    const isNext = e.key === 'ArrowRight' || e.key === 'ArrowDown';

    if (isPrev) {
      e.preventDefault();
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : tabIds.length - 1;
      setActiveTab(tabIds[prevIndex]);
    } else if (isNext) {
      e.preventDefault();
      const nextIndex = currentIndex < tabIds.length - 1 ? currentIndex + 1 : 0;
      setActiveTab(tabIds[nextIndex]);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <Loading variant="spinner" message="Loading admin dashboard..." />
      </div>
    );
  }

  return (
    <div className="super-admin">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(t => ({ ...t, show: false }))}
        />
      )}

      {/* Mobile nav: hamburger button visible only at <=900px (controlled via
          CSS). Toggling sets `mobileNavOpen` which adds `admin-sidebar--open`
          to the sidebar (CSS slides it in from the left as a drawer) and
          renders a backdrop. The useEffect on activeTab auto-closes the
          drawer when the user picks a tab. Discovered 2026-05-11 — the
          earlier pill-row attempt looked broken on iPhone (items stacked
          vertically and overlapped the table). */}
      <button
        type="button"
        className="admin-mobile-nav-toggle"
        aria-expanded={mobileNavOpen}
        aria-controls="admin-sidebar-nav"
        aria-label="Open navigation menu"
        onClick={() => setMobileNavOpen(true)}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
        <span>Menu</span>
      </button>

      {mobileNavOpen && (
        <button
          type="button"
          className="admin-mobile-nav-backdrop"
          aria-label="Close navigation menu"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {/* Phase 11 W1: vertical sidebar IA replaces the legacy horizontal
          .admin-tabs strip. Grouped sections — Dashboard / Manage / Data /
          Intelligence — scale to ~17 navigation items. Sidebar items keep
          role="tab" so the existing 45 admin tests (which query nav via
          getByRole('tab', ...)) keep working without edits. */}
      <div className="admin-shell">
        <nav
          id="admin-sidebar-nav"
          className={`admin-sidebar${mobileNavOpen ? ' admin-sidebar--open' : ''}`}
          aria-label="System Admin"
        >
          {/* Mobile-only close button at the top of the drawer. Hidden via
              CSS on desktop. */}
          <button
            type="button"
            className="admin-sidebar__close"
            aria-label="Close navigation menu"
            onClick={() => setMobileNavOpen(false)}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          {/* Brand + escape hatch. The admin console is mounted outside the
              app shell, so without this block there is literally no link back
              to COGSLOG proper — reported 2026-08-24. The wordmark and the
              "Back to app" link both resolve to the same destination. */}
          <div className="admin-sidebar__brand">
            <Link to={backToAppPath} className="admin-sidebar__brand-link" aria-label="COGSLOG home">
              <img
                src="/logos/wordmark/cogslog-wordmark-color.svg"
                alt="COGSLOG"
                className="admin-sidebar__wordmark"
                height="28"
              />
            </Link>
            <span className="admin-sidebar__brand-tag">System Admin</span>
            <Link to={backToAppPath} className="admin-sidebar__back">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to app
            </Link>
          </div>

          {/* role="tablist" moved off <nav> onto this inner wrapper so the
              brand block above is not an invalid non-tab child of a tablist.
              The accessible name stays "Admin navigation" — existing tests
              query getByRole('tablist', { name: /Admin navigation/i }).

              Phase 0 of the Operations rollout wraps every group in an
              AdminSidebarGroup accordion (collapsible header + hidden panel
              when collapsed). Leaves stay mounted so the flat "role=tab"
              hooks the surrounding tests query still resolve — visibility
              is controlled by the group panel's `hidden` attribute. */}
          <div
            className="admin-sidebar__tabs"
            role="tablist"
            aria-label="Admin navigation"
            aria-orientation="vertical"
          >
          {/* Labels for the mapped render come from renderTabLabel; the two
              count-bearing tabs (Organizations / Users) fall through to a
              live-count template while the rest resolve via a lookup.
              `data-invoices` stays "Invoice records" rather than "Invoices"
              so the legacy regex /Invoices/ in SuperAdmin.invoices.test.js
              still resolves uniquely to the Manage → Invoices tab
              (Pitfall 2 from 11-RESEARCH.md). */}
          {SIDEBAR_GROUP_IDS.map((groupId) => {
            const tabIds = SIDEBAR_GROUPS[groupId];
            return (
              <AdminSidebarGroup
                key={groupId}
                id={groupId}
                title={SIDEBAR_GROUP_TITLES[groupId]}
                isOpen={!!sidebarState[groupId]}
                onToggle={() => toggleSidebarGroup(groupId)}
              >
                {tabIds.map((tabId) => {
                  const label = renderTabLabel(tabId, { orgs, users });
                  const isActive = activeTab === tabId;
                  return (
                    <button
                      key={tabId}
                      id={`tab-${tabId}`}
                      role="tab"
                      aria-selected={isActive}
                      tabIndex={isActive ? 0 : -1}
                      className={`admin-sidebar__item ${isActive ? 'admin-sidebar__item--active' : ''}`}
                      onClick={() => setActiveTab(tabId)}
                      onKeyDown={handleTabKeyDown}
                    >
                      {label}
                    </button>
                  );
                })}
              </AdminSidebarGroup>
            );
          })}
          </div>
        </nav>

        {/* Main content column — every existing tabpanel below renders
            inside this wrapper. Phase 11 W1 leaves panel content exactly
            as it was; Waves 4+8 wire the new placeholder tabs. */}
        <div className="admin-shell__main">

      {/* Dashboard Tab — system-wide activity snapshot, default view */}
      {activeTab === 'dashboard' && (
        <div role="tabpanel" aria-labelledby="tab-dashboard">
          <section className="admin-section">
            <h3>System Activity</h3>
            <p className="admin-section__hint">
              At-a-glance view of users, orgs, and content. Click any user
              row below to focus the Users tab on that account.
            </p>
            {!dashboard ? (
              <Loading variant="spinner" message="Loading dashboard…" />
            ) : (
              <>
                <div className="admin-dashboard__kpis">
                  <DashboardKpi label="Users" value={dashboard.counts.users} sub={`${dashboard.counts.active_users} active · ${dashboard.counts.super_admins} super`} />
                  <DashboardKpi label="Organizations" value={dashboard.counts.organizations} sub={`+${dashboard.counts.organizations_last_30d} last 30d`} />
                  <DashboardKpi label="Businesses" value={dashboard.counts.businesses} />
                  <DashboardKpi label="Invoices" value={dashboard.counts.invoices} sub={`+${dashboard.counts.invoices_last_30d} last 30d`} />
                  <DashboardKpi label="Vendors" value={dashboard.counts.vendors} />
                  <DashboardKpi label="Categories" value={dashboard.counts.categories} />
                  <DashboardKpi label="Line items" value={dashboard.counts.line_items} />
                  <DashboardKpi label="New users (30d)" value={dashboard.counts.users_last_30d} />
                </div>

                <div className="admin-dashboard__columns">
                  <div className="admin-dashboard__col">
                    <h4 className="admin-dashboard__col-title">Recently active users</h4>
                    {dashboard.recently_active.length === 0 ? (
                      <div className="admin-cell-muted">No login activity recorded yet.</div>
                    ) : (
                      <ul className="admin-dashboard__list">
                        {dashboard.recently_active.map((u) => (
                          <li key={`active-${u.id}`}>
                            <button
                              type="button"
                              className="admin-dashboard__user-link"
                              onClick={() => handleFocusUser(u)}
                              aria-label={`Filter Users tab to ${u.email || u.username || `user ${u.id}`}`}
                            >
                              <span className="admin-dashboard__user-name">
                                {u.display_name || u.username || u.email || `User ${u.id}`}
                              </span>
                              <span className="admin-dashboard__user-meta">
                                {u.email}{u.is_super_admin ? ' · super' : ''}
                              </span>
                            </button>
                            <time className="admin-dashboard__when">
                              {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : ''}
                            </time>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="admin-dashboard__col">
                    <h4 className="admin-dashboard__col-title">Newly registered users</h4>
                    {dashboard.newly_registered.length === 0 ? (
                      <div className="admin-cell-muted">No registrations yet.</div>
                    ) : (
                      <ul className="admin-dashboard__list">
                        {dashboard.newly_registered.map((u) => (
                          <li key={`new-${u.id}`}>
                            <button
                              type="button"
                              className="admin-dashboard__user-link"
                              onClick={() => handleFocusUser(u)}
                              aria-label={`Filter Users tab to ${u.email || u.username || `user ${u.id}`}`}
                            >
                              <span className="admin-dashboard__user-name">
                                {u.display_name || u.username || u.email || `User ${u.id}`}
                              </span>
                              <span className="admin-dashboard__user-meta">{u.email}</span>
                            </button>
                            <time className="admin-dashboard__when">
                              {u.created_at ? new Date(u.created_at).toLocaleString() : ''}
                            </time>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="admin-dashboard__col">
                    <h4 className="admin-dashboard__col-title">Recent invoices</h4>
                    {dashboard.recent_invoices.length === 0 ? (
                      <div className="admin-cell-muted">No invoices yet.</div>
                    ) : (
                      <ul className="admin-dashboard__list">
                        {dashboard.recent_invoices.map((inv) => (
                          <li key={`inv-${inv.id}`}>
                            <span className="admin-dashboard__user-name">
                              {inv.invoice_number || `id ${inv.id}`}
                              {' '}
                              <span className="td-amt">${inv.total_amount}</span>
                            </span>
                            <span className="admin-dashboard__user-meta">
                              {inv.vendor_name || '—'} · {inv.business_name || '—'} · {inv.org_name || '—'}
                            </span>
                            <time className="admin-dashboard__when">
                              {inv.created_at ? new Date(inv.created_at).toLocaleString() : ''}
                            </time>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {/* Organizations Tab */}
      {activeTab === 'orgs' && (
        <div role="tabpanel" aria-labelledby="tab-orgs">
        <section className="admin-section">
          <h3>All Organizations</h3>
          {userOrgFilter && (
            <div
              role="status"
              aria-live="polite"
              className="admin-filter-banner"
            >
              <span>
                Showing only orgs for <strong>{userOrgFilter.userLabel}</strong>{' '}
                ({userOrgFilter.orgIds.length} of {orgs.length})
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setUserOrgFilter(null);
                  setOrgPage(1);
                }}
                aria-label="Clear user filter"
              >
                Clear filter
              </button>
            </div>
          )}
          <input
            type="text"
            placeholder="Search organizations..."
            value={orgSearch}
            onChange={(e) => {
              setOrgSearch(e.target.value);
              setOrgPage(1);
            }}
            className="input admin-search-input"
          />
          <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Name</th>
                <th scope="col">Slug</th>
                <th scope="col">Plan</th>
                <th scope="col">Members</th>
                <th scope="col">Active</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrgs.map((org) => (
                <tr key={org.id}>
                  <td><span className="td-amt">{org.id}</span></td>
                  <td>{org.name}</td>
                  <td>{org.slug}</td>
                  <td>{org.plan_type}</td>
                  <td>{org.member_count}</td>
                  <td>{org.is_active ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="admin-actions">
                      {org.is_active && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={async () => {
                            await startImpersonation({ id: org.id, name: org.name, slug: org.slug });
                            window.location.href = `/org/${org.slug}/dashboard`;
                          }}
                          aria-label={`View ${org.name}`}
                        >
                          View
                        </button>
                      )}
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          setActiveTab('invoices');
                          handleInvTabOrgChange(String(org.id));
                        }}
                        aria-label={`View invoices for ${org.name}`}
                      >
                        Invoices
                      </button>
                      {org.id !== 1 && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteOrg(org.id, org.name)}
                          aria-label={`Delete ${org.name}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pagination
            totalItems={filteredOrgs.length}
            currentPage={orgPage}
            perPage={orgPerPage}
            onPageChange={setOrgPage}
            onPerPageChange={(newPerPage) => {
              setOrgPerPage(newPerPage);
              setOrgPage(1);
            }}
          />
        </section>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div role="tabpanel" aria-labelledby="tab-users">
        <section className="admin-section">
          <h3>All Users</h3>
          <input
            type="text"
            placeholder="Search users..."
            value={userSearch}
            onChange={(e) => {
              setUserSearch(e.target.value);
              setUserPage(1);
            }}
            className="input admin-search-input"
          />
          <div className="tbl-wrap">
          <table className="admin-users-table">
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Username</th>
                <th scope="col">Display Name</th>
                <th scope="col">Email</th>
                <th scope="col">Orgs</th>
                <th scope="col">Super Admin</th>
                <th scope="col">Active</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user) => {
                const isSelf = currentUser && currentUser.id === user.id;
                const orgCount = Array.isArray(user.org_ids) ? user.org_ids.length : (user.org_count || 0);
                const userEmail = (user.email || '').toLowerCase();
                const userDomain = userEmail.includes('@') ? userEmail.split('@').pop() : '';
                const isGrantAction = !user.is_super_admin;
                const grantBlocked = isGrantAction && !(userDomain && allowedSuperAdminDomains.has(userDomain));
                const revokeBlocked = isSelf && user.is_super_admin;
                const buttonDisabled = grantBlocked || revokeBlocked;
                const tooltip = revokeBlocked
                  ? 'Cannot revoke your own super admin status'
                  : grantBlocked
                    ? (userDomain
                        ? `Cannot grant super admin: ${userDomain} is not in super_admin_allowed_domains. Add it in System Config first.`
                        : 'Cannot grant super admin: user has no email domain.')
                    : '';
                return (
                  <tr key={user.id}>
                    <td><span className="td-amt">{user.id}</span></td>
                    <td>{user.username}</td>
                    <td>{user.display_name}</td>
                    <td>{user.email}</td>
                    <td><span className="td-amt">{user.org_count}</span></td>
                    <td>
                      <button
                        className={`btn btn-sm ${user.is_super_admin ? 'btn-outline-danger' : 'btn-secondary'}`}
                        onClick={() => handleToggleSuperAdmin(user.id, user.email, user.is_super_admin)}
                        disabled={buttonDisabled}
                        title={tooltip}
                        aria-label={
                          user.is_super_admin
                            ? `Revoke super admin from ${user.email}`
                            : `Grant super admin to ${user.email}`
                        }
                      >
                        {user.is_super_admin ? 'Revoke' : 'Grant'}
                      </button>
                    </td>
                    <td>{user.is_active ? 'Yes' : 'No'}</td>
                    <td>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleViewUserOrgs(user)}
                        disabled={orgCount === 0}
                        title={orgCount === 0 ? 'User has no org memberships' : ''}
                        aria-label={`View ${orgCount} org${orgCount === 1 ? '' : 's'} for ${user.email}`}
                      >
                        View Orgs
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <Pagination
            totalItems={filteredUsers.length}
            currentPage={userPage}
            perPage={userPerPage}
            onPageChange={setUserPage}
            onPerPageChange={(newPerPage) => {
              setUserPerPage(newPerPage);
              setUserPage(1);
            }}
          />
        </section>
        </div>
      )}

      {/* Invoices Tab — org → business picker, paginated invoice list, multi-select ZIP */}
      {activeTab === 'invoices' && (
        <div role="tabpanel" aria-labelledby="tab-invoices">
        <section className="admin-section">
          <h3>Invoices by Business</h3>
          <p className="admin-section__hint">
            Pick an organization and one of its businesses to see all invoices for that business.
            Use the checkboxes to select multiple invoices and download them as a single ZIP.
          </p>

          <div className="admin-filter-row">
            <div className="field">
              <label className="label" htmlFor="invtab-org">Organization</label>
              <select
                id="invtab-org"
                className="input"
                value={invTabOrgId}
                onChange={(e) => handleInvTabOrgChange(e.target.value)}
              >
                <option value="">— Select org —</option>
                {orgs.filter((o) => o.is_active).map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name} (id={org.id})
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="invtab-biz">Business</label>
              <select
                id="invtab-biz"
                className="input"
                value={invTabBusinessId}
                onChange={(e) => {
                  setInvTabBusinessId(e.target.value);
                  setInvTabSelected(new Set());
                  setInvTabInvoicesPage(1);
                }}
                disabled={!invTabOrgId || invTabBusinessesLoading}
              >
                <option value="">
                  {invTabBusinessesLoading
                    ? 'Loading businesses…'
                    : (invTabOrgId ? '— Select business —' : '— Select org first —')}
                </option>
                {invTabBusinesses.map((biz) => (
                  <option key={biz.id} value={biz.id}>{biz.name}</option>
                ))}
              </select>
            </div>
          </div>

          {invTabOrgId && invTabBusinessId && (
            <>
              <div className="admin-toolbar">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={invTabSelected.size === 0 || invTabZipLoading}
                  onClick={handleDownloadSelectedZip}
                  aria-label={`Download ${invTabSelected.size} selected invoice${invTabSelected.size === 1 ? '' : 's'} as ZIP`}
                >
                  {invTabZipLoading
                    ? 'Building ZIP…'
                    : `Download ${invTabSelected.size} selected as ZIP`}
                </button>
                <span className="admin-toolbar__meta">
                  Total: <span className="td-amt">{invTabInvoicesTotal}</span> invoices
                </span>
              </div>

              {invTabInvoicesLoading ? (
                <Loading />
              ) : invTabInvoices.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state__icon" aria-hidden="true">📄</div>
                  <div className="empty-state__title">No invoices yet</div>
                  <div className="empty-state__description">
                    This business has no invoices recorded. Pick a different business
                    or have someone in this org upload an invoice.
                  </div>
                </div>
              ) : (
                <>
                  <div className="tbl-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th scope="col" className="admin-cell-checkbox">
                            <input
                              type="checkbox"
                              aria-label="Select all visible"
                              checked={invTabInvoices.length > 0 && invTabInvoices.every((inv) => invTabSelected.has(inv.id))}
                              onChange={toggleInvSelectAllVisible}
                            />
                          </th>
                          <th scope="col">Invoice #</th>
                          <th scope="col">Date</th>
                          <th scope="col">Vendor</th>
                          <th scope="col">Total</th>
                          <th scope="col">Status</th>
                          <th scope="col">PDF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invTabInvoices.map((inv) => (
                          <tr key={inv.id}>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`Select invoice ${inv.invoice_number || inv.id}`}
                                checked={invTabSelected.has(inv.id)}
                                onChange={() => toggleInvSelection(inv.id)}
                              />
                            </td>
                            <td>{inv.invoice_number || `id ${inv.id}`}</td>
                            <td>{inv.invoice_date || ''}</td>
                            <td>{inv.vendor_name || ''}</td>
                            <td><span className="td-amt">{inv.total_amount}</span></td>
                            <td>{inv.status}</td>
                            <td>
                              {inv.pdf_filename ? (
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => handleDownloadOne(inv.organization_id, inv.pdf_filename)}
                                  aria-label={`Download PDF for invoice ${inv.invoice_number || inv.id}`}
                                >
                                  Download
                                </button>
                              ) : (
                                <span className="admin-cell-muted">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    totalItems={invTabInvoicesTotal}
                    currentPage={invTabInvoicesPage}
                    perPage={invTabInvoicesPerPage}
                    onPageChange={setInvTabInvoicesPage}
                    onPerPageChange={(newPerPage) => {
                      setInvTabInvoicesPerPage(newPerPage);
                      setInvTabInvoicesPage(1);
                    }}
                  />
                </>
              )}
            </>
          )}
        </section>
        </div>
      )}

      {/* System Config Tab */}
      {activeTab === 'config' && config && (
        <div role="tabpanel" aria-labelledby="tab-config">
        <section className="admin-section">
          <h3>System Configuration</h3>
          <p className="admin-section__hint">
            Read-only entries below show the active runtime values from
            environment / Keycloak. Editable entries are persisted in the
            <code> system_settings </code>table and override any matching env vars.
          </p>

          <form
            className="admin-config-form"
            onSubmit={(e) => { e.preventDefault(); handleSaveConfig(); }}
          >
            <div className="field">
              <label className="label" htmlFor="cfg-domains">
                Super-admin allowed email domains
              </label>
              <input
                id="cfg-domains"
                type="text"
                className="input"
                value={configDraft.super_admin_allowed_domains || ''}
                placeholder="e.g. cogslog.com, example.org"
                onChange={(e) => setConfigDraft((d) => ({ ...d, super_admin_allowed_domains: e.target.value }))}
              />
              <span className="hint">
                Comma-separated. Only users whose email domain matches this list
                can be granted super admin. <strong>Empty = no one can be granted.</strong>
              </span>
            </div>

            <div className="field">
              <label className="label" htmlFor="cfg-reg-code">
                Registration access code
              </label>
              <input
                id="cfg-reg-code"
                type="text"
                className="input"
                value={configDraft.registration_code || ''}
                placeholder="(none — signup gate disabled)"
                onChange={(e) => setConfigDraft((d) => ({ ...d, registration_code: e.target.value }))}
              />
              <span className="hint">
                The code new users enter on the access-code page before signup.
                Setting empty disables the gate (anyone can sign up).
              </span>
            </div>

            <div className="field">
              <label className="label" htmlFor="cfg-terms">
                Terms version
              </label>
              <input
                id="cfg-terms"
                type="text"
                className="input"
                value={configDraft.terms_version || ''}
                placeholder="e.g. 2026-04-15"
                onChange={(e) => setConfigDraft((d) => ({ ...d, terms_version: e.target.value }))}
              />
              <span className="hint">
                Bumping this re-prompts every user to accept the latest terms on
                next login.
              </span>
            </div>

            <div className="admin-config-form__row">
              <div className="field">
                <label className="label" htmlFor="cfg-max-users">
                  Default max users per org
                </label>
                <input
                  id="cfg-max-users"
                  type="number"
                  min="0"
                  className="input"
                  value={configDraft.max_users_default ?? ''}
                  onChange={(e) => setConfigDraft((d) => ({ ...d, max_users_default: e.target.value }))}
                />
              </div>
              <div className="field">
                <label className="label" htmlFor="cfg-max-biz">
                  Default max businesses per org
                </label>
                <input
                  id="cfg-max-biz"
                  type="number"
                  min="0"
                  className="input"
                  value={configDraft.max_businesses_default ?? ''}
                  onChange={(e) => setConfigDraft((d) => ({ ...d, max_businesses_default: e.target.value }))}
                />
              </div>
              <div className="field">
                <label className="label" htmlFor="cfg-plan">
                  Default plan type
                </label>
                <select
                  id="cfg-plan"
                  className="input"
                  value={configDraft.default_plan_type || 'free'}
                  onChange={(e) => setConfigDraft((d) => ({ ...d, default_plan_type: e.target.value }))}
                >
                  <option value="free">free</option>
                  <option value="pro">pro</option>
                  <option value="enterprise">enterprise</option>
                </select>
              </div>
            </div>

            <h4 className="admin-config-form__readonly-heading">Read-only (env / Keycloak)</h4>
            <dl className="admin-config-readonly">
              <dt>Keycloak URL</dt>
              <dd><code>{config.keycloak_url || '—'}</code></dd>
              <dt>Keycloak realm</dt>
              <dd><code>{config.keycloak_realm || '—'}</code></dd>
              <dt>OCR enabled</dt>
              <dd>{config.ocr_enabled ? 'Yes' : 'No'}</dd>
            </dl>

            <div className="admin-config-form__actions">
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={!configIsDirty || configSaving}
              >
                {configSaving ? 'Saving…' : 'Save changes'}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={!configIsDirty || configSaving}
                onClick={() => setConfigDraft({
                  super_admin_allowed_domains: config.super_admin_allowed_domains || '',
                  registration_code: config.registration_code || '',
                  terms_version: config.terms_version || '',
                  max_users_default: config.max_users_default ?? '',
                  max_businesses_default: config.max_businesses_default ?? '',
                  default_plan_type: config.default_plan_type || 'free',
                })}
              >
                Discard
              </button>
            </div>
          </form>
        </section>
        </div>
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && billing && (
        <div role="tabpanel" aria-labelledby="tab-billing">
        <section className="admin-section">
          <h3>Billing Overview</h3>
          <div className="billing-summary">
            <div className="stat-card">
              <span className="stat-label">Total Organizations</span>
              <span className="stat-value td-amt">{billing.total_orgs}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Total Users</span>
              <span className="stat-value td-amt">{billing.total_users}</span>
            </div>
            {billing.plan_counts && Object.entries(billing.plan_counts).map(([plan, count]) => (
              <div className="stat-card" key={plan}>
                <span className="stat-label">{plan} plan</span>
                <span className="stat-value td-amt">{count}</span>
              </div>
            ))}
          </div>

          {billing.active_plans && billing.active_plans.length > 0 && (
            <>
              <h4>Active Plans</h4>
              <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Organization</th>
                    <th scope="col">Plan</th>
                    <th scope="col">Status</th>
                    <th scope="col">Period</th>
                  </tr>
                </thead>
                <tbody>
                  {billing.active_plans.map((plan) => (
                    <tr key={plan.id}>
                      <td>{plan.org_name}</td>
                      <td>{plan.plan_name}</td>
                      <td>{plan.status}</td>
                      <td>{plan.period_start} - {plan.period_end}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </section>
        </div>
      )}

      {/* Phase 11 W4: Data sidebar items render the AdminDataBrowser shell
          (built in Wave 2) bound to the cross-org `/admin/data/<entity>`
          endpoints (built in Wave 3). One parameterized block replaces the
          8 Wave-1 "Coming soon" placeholders. The `key` prop forces the
          shell to remount on tab switch so search/page state resets cleanly.
          Wave 8 wires the remaining Intelligence panels — Price ranges,
          Audit log, Feedback — which still render placeholders below. */}
      {Object.prototype.hasOwnProperty.call(DATA_BROWSER_CONFIGS, activeTab) && (() => {
        const cfg = DATA_BROWSER_CONFIGS[activeTab];
        return (
          <div
            key={activeTab}
            role="tabpanel"
            aria-labelledby={`tab-${activeTab}`}
          >
            <AdminDataBrowser
              endpoint={(params) => api.adminDataList(adminApiPrefix, cfg.entity, params)}
              onExport={(params) => api.adminDataExport(adminApiPrefix, cfg.entity, params)}
              columns={cfg.columns}
              searchPlaceholder={cfg.placeholder}
            />
          </div>
        );
      })()}

      {/* Phase 11 W8: Intelligence → Price ranges sub-page. Cross-vendor
          cross-org normalized $/base-unit aggregation. Cheapest-vendor
          drill button switches the active tab to Data → Line items so the
          operator can see the underlying invoices. */}
      {activeTab === 'price-ranges' && (
        <div
          key="price-ranges"
          role="tabpanel"
          aria-labelledby="tab-price-ranges"
        >
          <PriceRangesView
            adminApiPrefix={adminApiPrefix}
            onDrillToVendor={() => {
              // Drill: switch to the Data → Line items tab. v1 doesn't pass
              // a vendor filter through (AdminDataBrowser owns search state)
              // — the operator can search by vendor name once they land on
              // the Line items panel. Wave 9+ can wire a real filter prop.
              setActiveTab('data-line-items');
            }}
          />
        </div>
      )}

      {/* PR A4 hotfix — Analytics renders inline in the sidebar tabpanel
          so the sidebar stays visible while the operator is in the console. */}
      {activeTab === 'analytics' && (
        <div role="tabpanel" aria-labelledby="tab-analytics">
          <SuperAdminAnalytics />
        </div>
      )}

      {/* Operations → CRM. Full ops panel — health tile, mappings table,
          actions bar, failures panel, audit panel — lives in SuperAdminCrm.
          The component owns its own polling + drilldown drawer so the shell
          only forwards the admin API prefix. */}
      {activeTab === 'crm' && (
        <SuperAdminCrm adminApiPrefix={adminApiPrefix} />
      )}

      {/* Operations subsystems. Analytics rollup + Feedback→GitHub have
          full panels (GH #229 Phase 4b). PDF storage + Email SES remain as
          <ComingSoon /> placeholders until backend surfacing lands. */}
      {activeTab === 'ops-analytics-rollup' && (
        <div
          role="tabpanel"
          aria-labelledby="tab-ops-analytics-rollup"
          data-testid="tabpanel-ops-analytics-rollup"
        >
          <OpsAnalyticsRollupPanel adminApiPrefix={adminApiPrefix} />
        </div>
      )}
      {activeTab === 'ops-feedback-github' && (
        <div
          role="tabpanel"
          aria-labelledby="tab-ops-feedback-github"
          data-testid="tabpanel-ops-feedback-github"
        >
          <OpsFeedbackGithubPanel adminApiPrefix={adminApiPrefix} />
        </div>
      )}
      {activeTab === 'ops-email-intake' && (
        <div
          role="tabpanel"
          aria-labelledby="tab-ops-email-intake"
          data-testid="tabpanel-ops-email-intake"
        >
          <OpsEmailIngestPanel adminApiPrefix={adminApiPrefix} />
        </div>
      )}
      {[
        [
          'ops-pdf-storage',
          'PDF storage',
          'S3 vs local backend status + PDF migration button coming soon.',
        ],
        [
          'ops-email',
          'Email (SES)',
          'Send tally + recent failures coming soon.',
        ],
      ].map(([id, title, description]) => (
        activeTab === id && (
          <div
            key={id}
            role="tabpanel"
            aria-labelledby={`tab-${id}`}
            data-testid={`tabpanel-${id}`}
          >
            <ComingSoon title={title} description={description} />
          </div>
        )
      ))}

      {/* System group — Health dashboard. Combines container-level state
          (via read-only docker socket) with AWS managed-service state
          (RDS / ElastiCache / S3 / SES / SQS). All read-only. */}
      {activeTab === 'health' && (
        <div
          role="tabpanel"
          aria-labelledby="tab-health"
          data-testid="tabpanel-health"
        >
          <SuperAdminHealth adminApiPrefix={adminApiPrefix} />
        </div>
      )}

      {/* Support group — Audit log + Feedback. Both are full-page components
          that ship at their own admin routes (`${adminPath}/audit-log` and
          `${adminPath}/feedback`); rendering them inline here means clicking
          the sidebar tab uses the same page an operator gets from a direct
          bookmark. Both pull `adminApiPrefix` from AuthContext themselves —
          no props needed. */}
      {activeTab === 'audit-log' && (
        <div
          role="tabpanel"
          aria-labelledby="tab-audit-log"
          data-testid="tabpanel-audit-log"
        >
          <AuditLogViewer />
        </div>
      )}
      {activeTab === 'feedback' && (
        <div
          role="tabpanel"
          aria-labelledby="tab-feedback"
          data-testid="tabpanel-feedback"
        >
          <FeedbackAdmin />
        </div>
      )}

        </div>{/* /.admin-shell__main */}
      </div>{/* /.admin-shell */}
    </div>
  );
}

export default SuperAdmin;
